import type { ActionPanelVariant } from './types'

/**
 * AG Grid's horizontal cell padding, per side — the part of the column the
 * panel itself cannot use. Measured in the running grid.
 */
export const CELL_SIDE_PADDING = 15

/**
 * The cell's own column border, on its right.
 *
 * Transparent and so invisible, but a real pixel of the column: AG Grid's
 * `columnBorder` theme default is `1px solid transparent`, and the cell is
 * `border-box`, so it comes out of the width rather than adding to it.
 */
export const CELL_COLUMN_BORDER_WIDTH = 1

/**
 * The pinned-column divider, on the cell's left.
 *
 * The action column is the first right-pinned column, and AG Grid draws this
 * border on that *cell* — inside the column's own width — where in the header
 * row it draws the same border on the container instead. So the panel pays for
 * it and the header does not, which is why the two halves of the column agree
 * on their right edge but not on their left.
 *
 * Shared with `gridTheme`, which sets the border itself: two places naming the
 * same pixels, and a width that silently stopped matching would cost three
 * pixels of panel with nothing on screen to explain it.
 */
export const PINNED_COLUMN_BORDER_WIDTH = 2

/**
 * Everything in the column that is not the panel: the cell's padding either
 * side, and the two borders that sit inside its width.
 *
 * The borders are easy to miss — one is transparent and the other looks like it
 * belongs to the grid rather than to this cell — and missing them costs exactly
 * the amount that stops the action icons reaching the column's right edge.
 */
export const CELL_CHROME_WIDTH =
  CELL_SIDE_PADDING * 2 + CELL_COLUMN_BORDER_WIDTH + PINNED_COLUMN_BORDER_WIDTH

/**
 * Export to share with styled components
 */
export const GAP_WIDTH = 8
export const ICON_BUTTON_SIZE = 36
/**
 * The header chevron, a notch smaller than the action icons below it.
 *
 * Deliberately not `ICON_BUTTON_SIZE`: it is the grid's own control rather than
 * one of the row's actions, and the prototype draws it smaller to say so. It
 * costs nothing to size independently because the header never sets the
 * column's width — that comes from the form and the menu alone.
 */
export const HEADER_TOGGLE_SIZE = 28
export const CONTROL_HEIGHT = ICON_BUTTON_SIZE

/**
 * How many icon slots the menu's inline row has, per variant.
 *
 * The ellipsis takes a slot of its own, so exactly `N` actions all render
 * inline, while `N + 1` put `N - 1` inline and hand the rest to the dropdown —
 * which is why the smallest dropdown holds two. `narrow` is that same rule at
 * one slot, so nothing has to branch on the variant to collapse.
 *
 * A limit rather than "as many as are registered": actions arrive across three
 * epics and more are expected, so a `wide` that never reaches its limit today
 * is a fact about the current set, not the rule.
 *
 * `minimized` is undefined rather than zero, which is not handled well.
 */
export const MAX_ACTION_MENU_SLOTS: Record<
  Exclude<ActionPanelVariant, 'minimized'>,
  number
> = {
  narrow: 1,
  wide: 4,
}
// Width required by "Raise Bid", the longest CTA shown next to amount input.
export const WIDE_CTA_WIDTH = 96
export const PANEL_INPUT_WIDTH = 87
// Wide-variant ActionForm width, dictated by the "input + text button" layout.
export const WIDE_FORM_WIDTH = PANEL_INPUT_WIDTH + GAP_WIDTH + WIDE_CTA_WIDTH
/**
 * Narrow-variant ActionForm width: the CTA collapsed to its icon, no amount
 * input. The same square as the menu's buttons, so the panel's two halves read
 * as one row of controls rather than two sizes of them.
 */
export const NARROW_FORM_WIDTH = ICON_BUTTON_SIZE
// Per the prototype: 36px controls with 9px of clearance above and below.
export const PANEL_ROW_HEIGHT = 54
// Splits the panel's two halves, at the height of the controls either side of
// it — inset from the row borders, per the prototype.
export const DIVIDER_WIDTH = 1

/**
 * The clearance either side of the divider. It matches the cell's own side
 * padding rather than `GAP_WIDTH`, so the divider is spaced like an edge of the
 * panel — which is what it is, to each half — rather than like the tighter gap
 * that separates controls within a half.
 */
export const DIVIDER_SPACING = CELL_SIDE_PADDING

/**
 * The form's span, per variant.
 *
 * Shared with the CSS rather than restated there: the form takes an explicit
 * width — the bid states render inside Formik's shrink-to-fit form element,
 * where a percentage resolves against the content instead of the cell — so the
 * column's width and the form's have to be the same absolute value.
 *
 * `minimized` never renders, so we don't care what it gets.
 */
export const getActionFormWidth = (variant: ActionPanelVariant): number =>
  variant === 'wide' ? WIDE_FORM_WIDTH : NARROW_FORM_WIDTH

/**
 * The action column's width, as a pure function of what the panel is showing.
 *
 * `actionCount` is an input so the width follows the registry's length.
 *
 * It pays for the slots the menu renders rather than for every action it holds:
 * past the variant's limit the extras are behind the ellipsis, which is one
 * button however many it hides. So the column stops widening at the limit.
 *
 * `minimized` never renders, so we return null (vs zero, which is not handled
 * well).
 */
export const getActionPanelWidth = ({
  actionCount,
  variant,
}: {
  actionCount: number
  variant: ActionPanelVariant
}): number | null => {
  if (variant === 'minimized') return null

  return (
    CELL_CHROME_WIDTH +
    getActionFormWidth(variant) +
    getMenuWidth(Math.min(actionCount, MAX_ACTION_MENU_SLOTS[variant]))
  )
}

const getMenuWidth = (slotCount: number): number => {
  if (slotCount <= 0) return 0

  return (
    DIVIDER_SPACING * 2 +
    DIVIDER_WIDTH +
    slotCount * ICON_BUTTON_SIZE +
    (slotCount - 1) * GAP_WIDTH
  )
}
